/*
 * Forge Maestro
 * (c) Honeywell
 */

import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames/bind';
import React, { useState, Children } from 'react';
import { isNotNil } from '../../utils/common/is-nil.js';
import '../../utils/responsive/breakpoint/breakpoint-boundaries.const.js';
import '../../utils/responsive/breakpoint/breakpoint.enum.js';
import { Button } from '../Button/Button.js';
import '../Button/LinkButton.js';
import '../Icon/icons/AlertCircleIcon.js';
import { AlertIcon } from '../Icon/icons/AlertIcon.js';
import '../Icon/icons/ArrowDownIcon.js';
import '../Icon/icons/ArrowDownDiagonalIcon.js';
import '../Icon/icons/ArrowLeftIcon.js';
import '../Icon/icons/ArrowRightIcon.js';
import '../Icon/icons/ArrowUpIcon.js';
import '../Icon/icons/ArrowUpDiagonalIcon.js';
import '../Icon/icons/AreaChart.js';
import '../Icon/icons/AttachIcon.js';
import '../Icon/icons/AvatarIcon.js';
import '../Icon/icons/BellOffIcon.js';
import '../Icon/icons/BellIcon.js';
import '../Icon/icons/DividerIcon.js';
import '../Icon/icons/BookmarkIcon.js';
import '../Icon/icons/ProductPlusIcon.js';
import '../Icon/icons/HoneywellLogoIcon.js';
import '../Icon/icons/CalendarIcon.js';
import '../Icon/icons/CameraIcon.js';
import '../Icon/icons/ChatIcon.js';
import '../Icon/icons/CheckIcon.js';
import '../Icon/icons/ChevronDownIcon.js';
import '../Icon/icons/ChevronLeftIcon.js';
import '../Icon/icons/ChevronRightIcon.js';
import '../Icon/icons/ChevronUpIcon.js';
import '../Icon/icons/ChevronsDownIcon.js';
import '../Icon/icons/ChevronsLeftIcon.js';
import '../Icon/icons/ChevronsRightIcon.js';
import '../Icon/icons/ChevronsUpIcon.js';
import '../Icon/icons/ClockIcon.js';
import { CloseIcon } from '../Icon/icons/CloseIcon.js';
import '../Icon/icons/CloseCircleIcon.js';
import '../Icon/icons/ColumnsIcon.js';
import '../Icon/icons/CommentIcon.js';
import '../Icon/icons/CopyIcon.js';
import '../Icon/icons/DensityLeastIcon.js';
import '../Icon/icons/DensityMediumIcon.js';
import '../Icon/icons/DensityMostIcon.js';
import '../Icon/icons/DescriptionIcon.js';
import '../Icon/icons/EditIcon.js';
import { ErrorIcon } from '../Icon/icons/ErrorIcon.js';
import '../Icon/icons/FailedIcon.js';
import '../Icon/icons/FavoriteIcon.js';
import '../Icon/icons/FilterIcon.js';
import '../Icon/icons/FilterAscendingSort.js';
import '../Icon/icons/FilterDescendingSort.js';
import '../Icon/icons/FilterRemove.js';
import '../Icon/icons/GrabberIcon.js';
import '../Icon/icons/GraphIcon.js';
import '../Icon/icons/GridIcon.js';
import '../Icon/icons/Group.js';
import '../Icon/icons/HelpIcon.js';
import '../Icon/icons/HideIcon.js';
import '../Icon/icons/HistoryIcon.js';
import '../Icon/icons/HomeIcon.js';
import '../Icon/icons/ImportIcon.js';
import { InfoIcon } from '../Icon/icons/InfoIcon.js';
import '../Icon/icons/LinkIcon.js';
import '../Icon/icons/LockIcon.js';
import '../Icon/icons/MaximizeIcon.js';
import '../Icon/icons/MenuIcon.js';
import '../Icon/icons/MinimizeIcon.js';
import '../Icon/icons/MinusIcon.js';
import '../Icon/icons/ModelCreationIcon.js';
import '../Icon/icons/MonitorIcon.js';
import '../Icon/icons/MoreHorizontalIcon.js';
import '../Icon/icons/MoreVerticalIcon.js';
import '../Icon/icons/OpenInNewIcon.js';
import '../Icon/icons/PauseIcon.js';
import '../Icon/icons/PinIcon.js';
import '../Icon/icons/PinAltIcon.js';
import '../Icon/icons/PlayIcon.js';
import '../Icon/icons/PlaceholderIcon.js';
import '../Icon/icons/PlusIcon.js';
import '../Icon/icons/ProgressIcon.js';
import '../Icon/icons/ProgressAltIcon.js';
import '../Icon/icons/QuestionIcon.js';
import '../Icon/icons/RecentIcon.js';
import '../Icon/icons/RedoIcon.js';
import '../Icon/icons/RefreshIcon.js';
import '../Icon/icons/SearchIcon.js';
import '../Icon/icons/SendIcon.js';
import '../Icon/icons/SettingsIcon.js';
import '../Icon/icons/ShareAltIcon.js';
import '../Icon/icons/ShareIcon.js';
import '../Icon/icons/ShowIcon.js';
import '../Icon/icons/SignOutIcon.js';
import '../Icon/icons/SliderIcon.js';
import '../Icon/icons/SortAscending.js';
import '../Icon/icons/SortDescending.js';
import '../Icon/icons/Stop.js';
import { SuccessIcon } from '../Icon/icons/SuccessIcon.js';
import '../Icon/icons/TimePeriodIcon.js';
import '../Icon/icons/ToolJiraIcon.js';
import '../Icon/icons/TrashIcon.js';
import '../Icon/icons/TrendIcon.js';
import '../Icon/icons/UndoIcon.js';
import '../Icon/icons/UnlinkIcon.js';
import '../Icon/icons/UnlockIcon.js';
import '../Icon/icons/UnpinIcon.js';
import '../Icon/icons/UnpinAltIcon.js';
import styles from './Banner.module.scss.js';
import { BANNER_KEYBOARD_VALUES } from './BannerProps.interface.js';

const cx = classNames.bind(styles);
const ANIMATION_DURATION = 400;
const Banner = (props) => {
    const { open = true, title, bannerType, content, note, children, onClose, className, bannerVariant = 'primary', closeLabel = 'Close', animate = true, } = Object.assign({}, props);
    const [animateClass, setAnimate] = useState('enter-animate');
    let icon;
    switch (bannerType) {
        case 'success':
            icon = (jsx(SuccessIcon, { className: cx((styles['icon-class'], styles['circle-hw'])) }));
            break;
        case 'critical':
            icon = (jsx(ErrorIcon, { className: cx((styles['icon-class'], styles['circle-hw'])) }));
            break;
        case 'warning':
            icon = jsx(AlertIcon, { className: styles['icon-class'] });
            break;
        case 'info':
            icon = (jsx(InfoIcon, { className: cx((styles['icon-class'], styles['circle-hw'])) }));
            break;
        default:
            icon = null;
    }
    const handleKeyDown = (e) => {
        if (e.key === BANNER_KEYBOARD_VALUES.ESCAPE ||
            e.key === BANNER_KEYBOARD_VALUES.ENTER ||
            e.key === BANNER_KEYBOARD_VALUES.SPACE) {
            handleClose();
        }
    };
    const rightSideButtonElements = Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type !== Button) {
            throw new Error('Invalid child component.Children should be of type Button');
        }
        return jsx(Fragment, { children: child });
    });
    const handleClose = () => {
        if (animate) {
            setAnimate('exit-animate');
            const myTimeout = setTimeout(() => {
                if (isNotNil(onClose)) {
                    onClose();
                    clearTimeout(myTimeout);
                }
            }, ANIMATION_DURATION);
        }
        else {
            if (isNotNil(onClose)) {
                onClose();
            }
        }
    };
    return open ? (jsxs("section", Object.assign({ className: cx(styles.banner, styles[bannerVariant], {
            [styles[animateClass]]: animate,
            [styles['content-padding']]: bannerVariant === 'inline' || bannerVariant === 'onPage',
            [styles[`${bannerVariant}-${bannerType}`]]: (bannerVariant === 'inline' || bannerVariant === 'onPage') &&
                bannerType,
        }, className), role: "banner" }, { children: [jsxs("div", Object.assign({ className: cx(styles.section1) }, { children: [bannerType ? (jsx("div", Object.assign({ className: cx(styles['banner-type'], styles[bannerType], {
                            [styles[`${bannerVariant}-${bannerType}`]]: bannerVariant === 'primary',
                            [styles['inline-onpage-icon']]: bannerVariant === 'inline' || bannerVariant === 'onPage',
                            [styles['primary-icon']]: bannerVariant === 'primary',
                            [styles['secondary-icon']]: bannerVariant === 'secondary',
                        }) }, { children: icon }))) : undefined, jsxs("div", Object.assign({ className: styles.contents }, { children: [jsx("h3", Object.assign({ className: styles.title }, { children: title })), content ? jsx("p", Object.assign({ className: styles.content }, { children: content })) : '', note ? jsx("p", Object.assign({ className: styles.note }, { children: note })) : ''] }))] })), jsxs("div", Object.assign({ className: styles.section2 }, { children: [rightSideButtonElements, isNotNil(onClose) ? (jsx(Button, { "data-testId": "close-button", icon: jsx(CloseIcon, { strokeWidth: 2 }), label: closeLabel, size: "regular", variant: "quiet-secondary", onClick: handleClose, onKeyDown: handleKeyDown, className: cx({
                            [styles['banner-close']]: bannerVariant === 'primary',
                        }) })) : null] }))] }))) : null;
};

export { Banner };
//# sourceMappingURL=Banner.js.map
